(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (from, to, value) => {
    const x = clamp((value - from) / Math.max(to - from, 0.0001));
    return x * x * (3 - 2 * x);
  };
  const parseFrames = (root, selector) => {
    const element = root.querySelector(selector);
    if (!element) return [];
    try { return JSON.parse(element.textContent || '[]'); } catch { return []; }
  };
  const setPicture = (picture, desktopUrl, mobileUrl) => {
    if (!picture) return;
    const source = picture.querySelector('source');
    const image = picture.querySelector('img');
    if (source && source.srcset !== mobileUrl) source.srcset = mobileUrl;
    if (image && image.src !== desktopUrl) image.src = desktopUrl;
  };
  const init = (root) => {
    if (root.dataset.sequenceReady === 'true') return;
    root.dataset.sequenceReady = 'true';
    const desktopFrames = parseFrames(root, '[data-fur-frames-desktop]');
    const mobileFrames = parseFrames(root, '[data-fur-frames-mobile]');
    if (!desktopFrames.length || desktopFrames.length !== mobileFrames.length) return;
    const currentPicture = root.querySelector('[data-fur-frame-current]');
    const nextPicture = root.querySelector('[data-fur-frame-next]');
    const intro = root.querySelector('[data-fur-intro]');
    const chapter = root.querySelector('[data-fur-chapter]');
    const count = root.querySelector('[data-fur-count]');
    const meter = root.querySelector('[data-fur-meter]');
    const breeze = root.querySelector('[data-fur-breeze]');
    const steps = [...root.querySelectorAll('[data-fur-step]')];
    const labels = steps.map((step) => step.dataset.label || '');
    const mobileMedia = matchMedia('(max-width: 749px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const windStrength = clamp(Number(root.dataset.wind) || 0, 0, 1.6);
    let breezeAnimation = null;
    if (breeze && windStrength > 0 && !reducedMotion.matches && typeof breeze.animate === 'function') {
      breeze.style.animation = 'none';
      breezeAnimation = breeze.animate([
        { filter: 'brightness(1)', transform: 'translate3d(0,0,0) skewX(0deg) scale(1)', offset: 0 },
        { filter: `brightness(${1 + (0.018 * windStrength)})`, transform: `translate3d(${0.08 * windStrength}%,-${0.05 * windStrength}%,0) skewX(${0.08 * windStrength}deg) scale(${1 + (0.0015 * windStrength)})`, offset: 0.45 },
        { filter: `brightness(${1 - (0.005 * windStrength)})`, transform: `translate3d(-${0.04 * windStrength}%,${0.03 * windStrength}%,0) skewX(-${0.05 * windStrength}deg) scale(${1 + (0.0005 * windStrength)})`, offset: 0.63 },
        { filter: 'brightness(1)', transform: 'translate3d(0,0,0) skewX(0deg) scale(1)', offset: 1 }
      ], { duration: 5200, easing: 'ease-in-out', iterations: Infinity });
    }
    if (breeze && windStrength === 0) breeze.style.animation = 'none';
    let currentIndex = -1;
    let nextIndex = -1;
    let raf = 0;
    let pointerRaf = 0;
    let frameQueued = false;
    let destroyed = false;
    let preloadTimer = 0;
    let targetX = innerWidth * 0.68;
    let targetY = innerHeight * 0.42;
    let currentX = targetX;
    let currentY = targetY;
    const heroIsActive = () => {
      const rect = root.getBoundingClientRect();
      return rect.top <= 0 && rect.bottom >= innerHeight;
    };
    const drawPointer = () => {
      currentX += (targetX - currentX) * 0.2;
      currentY += (targetY - currentY) * 0.2;
      root.style.setProperty('--fur-cursor-x', `${currentX}px`);
      root.style.setProperty('--fur-cursor-y', `${currentY}px`);
      if (Math.hypot(targetX - currentX, targetY - currentY) > 0.15) pointerRaf = requestAnimationFrame(drawPointer);
    };
    const movePointer = (event) => {
      if (!finePointer.matches || !heroIsActive()) {
        root.classList.remove('is-inspecting', 'is-interactive');
        return;
      }
      targetX = event.clientX;
      targetY = event.clientY;
      root.classList.add('is-inspecting');
      root.classList.toggle('is-interactive', event.target instanceof Element && Boolean(event.target.closest('a, button')));
      cancelAnimationFrame(pointerRaf);
      pointerRaf = requestAnimationFrame(drawPointer);
    };
    const hidePointer = (event) => {
      if (event.relatedTarget === null) root.classList.remove('is-inspecting', 'is-interactive');
    };
    const hidePointerOutsideHero = () => {
      if (!heroIsActive()) root.classList.remove('is-inspecting', 'is-interactive');
    };
    const render = () => {
      frameQueued = false;
      if (destroyed || reducedMotion.matches) return;
      const rect = root.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, rect.height - innerHeight));
      const coatOnlyProgress = 0.6;
      const frameValue = progress <= coatOnlyProgress
        ? (progress / coatOnlyProgress) * 23
        : 23 + ((progress - coatOnlyProgress) / (1 - coatOnlyProgress)) * (desktopFrames.length - 24);
      const nextCurrent = Math.floor(frameValue);
      const nextNext = Math.min(nextCurrent + 1, desktopFrames.length - 1);
      const frameMix = nextNext === nextCurrent ? 0 : frameValue - nextCurrent;
      if (nextCurrent !== currentIndex) {
        currentIndex = nextCurrent;
        setPicture(currentPicture, desktopFrames[currentIndex], mobileFrames[currentIndex]);
      }
      if (nextNext !== nextIndex) {
        nextIndex = nextNext;
        setPicture(nextPicture, desktopFrames[nextIndex], mobileFrames[nextIndex]);
      }
      const frameScale = (index) => {
        if (index < 24) return 1;
        if (index < 30) return 1 + (index - 24) * 0.024;
        if (index < 35) return 1 + (index - 30) * 0.028;
        return 1 + (index - 35) * 0.028;
      };
      const sameImage = desktopFrames[nextCurrent] === desktopFrames[nextNext];
      const currentScale = sameImage
        ? frameScale(nextCurrent) + (frameScale(nextNext) - frameScale(nextCurrent)) * frameMix
        : frameScale(nextCurrent);
      if (currentPicture) currentPicture.style.transform = `scale(${currentScale.toFixed(4)})`;
      if (nextPicture) {
        nextPicture.style.transform = `scale(${frameScale(nextNext).toFixed(4)})`;
        nextPicture.style.opacity = sameImage ? '0' : smooth(0, 1, frameMix).toFixed(4);
      }
      if (intro) intro.style.opacity = (1 - smooth(0.06, 0.17, progress)).toFixed(4);
      if (breeze) breeze.style.opacity = clamp(1 - progress / 0.045).toFixed(4);
      if (meter) meter.style.transform = `scaleY(${progress})`;
      root.style.setProperty('--fur-sequence-progress', progress.toFixed(5));
      root.style.setProperty('--fur-sequence-wind', root.dataset.wind || '1');
      const active = progress < 0.15 ? 0 : progress < 0.3 ? 1 : progress < 0.45 ? 2 : progress < 0.61 ? 3 : progress < 0.86 ? 4 : 5;
      if (chapter) chapter.textContent = labels[active] || '';
      if (count) count.textContent = `${String(active + 1).padStart(2, '0')} / 06`;
      steps.forEach((step, index) => step.classList.toggle('is-active', index === active));
    };
    const queueRender = () => {
      if (frameQueued) return;
      frameQueued = true;
      raf = requestAnimationFrame(render);
    };
    const preload = () => {
      if (root.dataset.preload !== 'true' || destroyed) return;
      const frames = mobileMedia.matches ? mobileFrames : desktopFrames;
      [...new Set(frames)].forEach((url) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
      });
    };
    addEventListener('scroll', queueRender, { passive: true });
    addEventListener('resize', queueRender, { passive: true });
    addEventListener('pointermove', movePointer, { passive: true });
    addEventListener('pointerout', hidePointer, { passive: true });
    addEventListener('scroll', hidePointerOutsideHero, { passive: true });
    mobileMedia.addEventListener('change', queueRender);
    setPicture(currentPicture, desktopFrames[0], mobileFrames[0]);
    setPicture(nextPicture, desktopFrames[1], mobileFrames[1]);
    render();
    preloadTimer = window.setTimeout(preload, 350);
    root._furSequenceDestroy = () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(pointerRaf);
      clearTimeout(preloadTimer);
      breezeAnimation?.cancel();
      removeEventListener('scroll', queueRender);
      removeEventListener('resize', queueRender);
      removeEventListener('pointermove', movePointer);
      removeEventListener('pointerout', hidePointer);
      removeEventListener('scroll', hidePointerOutsideHero);
      mobileMedia.removeEventListener('change', queueRender);
    };
  };
  const initAll = (scope = document) => scope.querySelectorAll('[data-fur-sequence]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-fur-sequence]');
    root?._furSequenceDestroy?.();
  });
})();
